"use client";
import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
// import { Helmet } from "react-helmet";
import { Badge } from "../../components/ui/badge";
import { getBlogByBlogURL } from "./services/blog.services";
import { clearBlogDetails } from "./blogSlice";
import { useAppDispatch, useAppSelector } from "../../_hooks/hooks";
import { motion } from "motion/react";
import dynamic from "next/dynamic";
import { ChevronDown } from "lucide-react";

const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);

export function BlogDetailPage({ initialData }: { initialData?: any }) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const BlogURL = params?.BlogURL as string;
  const fullURL =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`
      : "";

  const homeLoading = useAppSelector((state) => state.home.loading);
  const blogLoading = useAppSelector((state) => state.blog.loading);
  const storeBlogDetails = useAppSelector((state) => state.blog.BlogDetails);

  const BlogDetails =
    Object.keys(storeBlogDetails || {}).length > 0
      ? storeBlogDetails
      : initialData;

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqList = BlogDetails?.BlogFAQList || [];

  useEffect(() => {
    if (initialData && Object.keys(storeBlogDetails || {}).length === 0) {
      import("./blogSlice").then((mod) => {
        dispatch(mod.setBlogDetails(initialData));
      });
    } else if (BlogURL) {
      dispatch(getBlogByBlogURL(BlogURL));
    }
  }, [BlogURL, initialData]);

  useEffect(() => {
    // dispatch(getAllCMSPageData(""));
    return () => {
      dispatch(clearBlogDetails());
    };
  }, []);

  return (
    <>
      {(homeLoading || blogLoading) && <Preloader />}
      <div className="min-h-screen bg-white">
        <section className="relative min-h-screen flex items-end justify-center overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url("${BlogDetails?.BlogBannerImage}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 mt-25 mb-15">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
                <p className="text-[#C9A962] tracking-[0.4em] text-sm">BLOG</p>
              </div>

              <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight max-w-5xl mx-auto font-playfair">
                {/* We Don't Just Build Resorts
                <br /> */}
                <span className="text-[#C9A962]">{BlogDetails?.BlogTitle}</span>
              </h1>

              {/* <p
                className="text-white/95 text-xl md:text-2xl max-w-3xl mx-auto"
                dangerouslySetInnerHTML={{
                  __html: BlogDetails?.ShortDescription,
                }}
              /> */}
            </motion.div>
          </div>
        </section>
        <section className="py-10 md:py-20 bg-[#F9F7F4] relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="mb-10 text-2xl md:text-4xl font-semibold font-playfair text-[#1A1A1A]">
              {BlogDetails?.BlogTitle}
            </h2>
            <div
              className="leading-relaxed bulletin-section text-justify text-gray-700 Blog-detail"
              dangerouslySetInnerHTML={{
                __html: BlogDetails?.LongDescription,
              }}
            />
          </div>
        </section>

        {faqList.length > 0 && (
          <section className="py-12 md:py-24 bg-white border-t border-gray-100">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                  <p className="text-[#B42025] tracking-[0.3em] mb-3 text-sm uppercase font-semibold">
                    FAQ
                  </p>
                  <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] font-playfair">
                    Frequently Asked Questions
                  </h2>
                  <div className="w-20 h-1 bg-[#C9A962] mx-auto mt-4" />
                </div>

                <div className="space-y-4">
                  {faqList.map((faq: any, index: number) => {
                    const isOpen = openFAQ === index;
                    return (
                      <div
                        key={faq.ID || index}
                        className="bg-[#F9F7F4] rounded-xl overflow-hidden transition-all duration-300 border border-transparent hover:border-[#C9A962]/20"
                      >
                        <button
                          onClick={() => setOpenFAQ(isOpen ? null : index)}
                          className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#F9F7F4]/80 transition-colors"
                        >
                          <span className="text-md font-medium text-[#1A1A1A] pr-4">
                            {faq?.Question}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 text-[#B42025] transition-transform duration-300 flex-shrink-0 ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-6 py-2">
                            <p
                              className="text-sm text-gray-700 leading-relaxed"
                              dangerouslySetInnerHTML={{
                                __html: faq?.Answer,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
